import { useTranslate } from "@similarweb/i18n-provider";
import { Info24px } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Badge } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import type { PaymentPeriod } from "app/components/PackageSwitcher/consts/paymentPeriod";
import type { IChangePackageAddonInfo } from "app/components/PackageSwitcher/types/addonsTypes";

import { AddonsTooltipContent } from "./AddonsTooltipContent/AddonsTooltipContent";
import {
    SummaryDiscountItemText,
    SummaryDivider,
    SummaryHolder,
    SummaryItem,
    SummaryItemFlexColumn,
    SummaryItemText,
    SummaryTooltip,
    SummaryTotalItemText,
} from "./SummarySwitchCard.styled";

interface ISummarySwitchCardProps {
    targetPackageName: string;
    initialPackagePriceFormatted: string;
    hasAddons: boolean;
    addonsInfo: IChangePackageAddonInfo[];
    period: PaymentPeriod;
    addonsPriceFormatted?: string;
    isUpgradeToAnnual: boolean;
    annualDiscountFormatted?: string;
    totalPrice: string;
    taxFormatted?: string | null;
    taxPercentage: number | null;
}

export const SummarySwitchCard = ({
    targetPackageName,
    initialPackagePriceFormatted,
    hasAddons,
    addonsInfo,
    period,
    addonsPriceFormatted,
    isUpgradeToAnnual,
    annualDiscountFormatted,
    totalPrice,
    taxFormatted,
    taxPercentage,
}: ISummarySwitchCardProps) => {
    const { translate } = useTranslate();
    const taxPercentageFormatted = taxPercentage ? (taxPercentage * 100).toFixed(0) : null;
    return (
        <SummaryHolder>
            <Text
                fontVariant="captionTitle"
                color={colorsPalettes.carbon[300]}
            >
                {translate("buyer_journey.switch_package.summary.title")}
            </Text>
            <SummaryItemFlexColumn>
                <SummaryItem>
                    <SummaryItemText fontVariant="body2">{targetPackageName}</SummaryItemText>
                    <SummaryItemText fontVariant="body2">{initialPackagePriceFormatted}</SummaryItemText>
                </SummaryItem>
                {hasAddons ? (
                    <SummaryItem>
                        <Flex
                            alignItems="center"
                            gap="4px"
                        >
                            <SummaryItemText fontVariant="body2">
                                {translate("buyer_journey.switch_package.summary.addons.title")}
                            </SummaryItemText>
                            <SummaryTooltip
                                content={
                                    <AddonsTooltipContent
                                        addonsInfo={addonsInfo}
                                        period={period}
                                    />
                                }
                                placement="top"
                                delay={0}
                            >
                                <Badge
                                    bgColor={colorsPalettes.carbon[50]}
                                    color={colorsPalettes.carbon[400]}
                                    type="custom"
                                    data-automation="package-switcher-addons-badge"
                                >
                                    {addonsInfo.length}
                                </Badge>
                            </SummaryTooltip>
                        </Flex>
                        <SummaryItemText fontVariant="body2">{addonsPriceFormatted}</SummaryItemText>
                    </SummaryItem>
                ) : null}

                {isUpgradeToAnnual ? (
                    <SummaryItem>
                        <SummaryDiscountItemText fontVariant="body2">
                            {translate("buyer_journey.switch_package.summary.annual_discount.title")}
                        </SummaryDiscountItemText>
                        <SummaryDiscountItemText fontVariant="body2">{annualDiscountFormatted}</SummaryDiscountItemText>
                    </SummaryItem>
                ) : null}
            </SummaryItemFlexColumn>
            <SummaryDivider />
            <SummaryItemFlexColumn>
                {taxFormatted && taxPercentageFormatted ? (
                    <SummaryItem>
                        <SummaryItemText fontVariant="body2">
                            {translate("buyer_journey.switch_package.summary.vat", { vat: taxPercentageFormatted })}
                        </SummaryItemText>
                        <SummaryItemText fontVariant="body2">{taxFormatted}</SummaryItemText>
                    </SummaryItem>
                ) : null}

                <SummaryItem>
                    <Flex
                        alignItems="center"
                        gap="4px"
                    >
                        <SummaryTotalItemText fontVariant="body2">
                            {translate("buyer_journey.switch_package.summary.total.title")}
                        </SummaryTotalItemText>
                        {!taxFormatted ? (
                            <SummaryTooltip
                                content={
                                    <Text
                                        fontVariant="tooltip"
                                        color={colorsPalettes.bluegrey[100]}
                                    >
                                        {translate("buyer_journey.switch_package.summary.vat.not_included")}
                                    </Text>
                                }
                            >
                                <Info24px
                                    size="sm"
                                    color={colorsPalettes.carbon[200]}
                                />
                            </SummaryTooltip>
                        ) : null}
                    </Flex>
                    <SummaryTotalItemText fontVariant="body2">{totalPrice}</SummaryTotalItemText>
                </SummaryItem>
            </SummaryItemFlexColumn>
        </SummaryHolder>
    );
};
